import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, useAuthStore, type AuthUser } from '@/stores/authStore';
import type { OneClickReceipt, OneClickReceiptsResponse } from '@/types/kalshi';

/**
 * #314 — loads the durable one-click receipts for one ticker.
 *
 * Runs on mount and on every ticker change, which is what makes a page refresh
 * or a reopened game recover the receipts from durable audit data rather than
 * from the session-local acknowledgement that arm/disarm destroys.
 *
 * Staleness: every load takes a generation number, and a response is applied
 * only if its generation is still current. A ticker change or a refresh
 * supersedes whatever is in flight — so a slow response for the market the
 * trader just navigated away from can never paint over the one they are
 * looking at.
 *
 * The returned promise NEVER rejects. `refresh` is called from the one-click
 * submit path's `finally`; a rejection there would surface as an unhandled
 * rejection and could interleave with the submission-reset semantics that path
 * owns. Read failures land in `error` and nowhere else.
 */

export interface UseOneClickReceiptsArgs {
  ticker: string | null;
  enabled: boolean;
}

export interface UseOneClickReceiptsResult {
  receipts: OneClickReceipt[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/** #1025 slice 2 — whose receipts these are: the fields that advance the auth
 *  session epoch (user, org, role). No `status` term: a same-user loading blip
 *  is not a new identity, and sign-out already disables the card. The relay
 *  scopes receipts by user and org, so key mode is deliberately not here. */
export function receiptsIdentity(s: { user: AuthUser | null }): string | null {
  return s.user ? `${s.user.id}|${s.user.orgId ?? ''}|${s.user.role}` : null;
}

function receiptsUrl(ticker: string): string {
  return `/api/trader/one-click-receipts?${new URLSearchParams({ ticker }).toString()}`;
}

export function useOneClickReceipts({
  ticker,
  enabled,
}: UseOneClickReceiptsArgs): UseOneClickReceiptsResult {
  const [receipts, setReceipts] = useState<OneClickReceipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const identity = useAuthStore(receiptsIdentity);
  const identityRef = useRef(identity);

  // Monotonic generation. Incremented by every load; a response whose
  // generation is stale is dropped rather than applied.
  const generationRef = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    if (!enabled || !ticker) return;
    const generation = (generationRef.current += 1);
    setLoading(true);
    try {
      const res = await apiFetch(receiptsUrl(ticker));
      if (generation !== generationRef.current) return;
      if (!res.ok) {
        setReceipts([]);
        setError(`Could not load one-click receipts (HTTP ${res.status})`);
        return;
      }
      const body = (await res.json()) as OneClickReceiptsResponse;
      if (generation !== generationRef.current) return;
      setReceipts(body?.receipts ?? []);
      setError(null);
    } catch (err) {
      if (generation !== generationRef.current) return;
      // A failed READ says nothing about what rests at the venue, so the
      // previous receipts are dropped rather than left to look current.
      setReceipts([]);
      setError(err instanceof Error ? err.message : 'Could not load one-click receipts');
    } finally {
      if (generation === generationRef.current) setLoading(false);
    }
    // `identity` re-creates the loader, so the effect below reloads under the new one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ticker, identity]);

  useEffect(() => {
    if (!enabled || !ticker) {
      // Supersede anything in flight so a late response cannot paint receipts
      // onto a ticket that no longer targets a Kalshi market.
      generationRef.current += 1;
      // The dependent ticket became ineligible; keeping its old receipt would
      // misrepresent a different market as durable evidence. This is a
      // synchronous reset of local presentation state, not an external sync.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReceipts([]);
      setError(null);
      setLoading(false);
      return;
    }
    if (identityRef.current !== identity) {
      identityRef.current = identity;
      // #1025 slice 2 — the old identity's rows are not evidence for the new
      // one, even for the length of a read: supersede and clear first.
      generationRef.current += 1;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReceipts([]);
      setError(null);
    }
    void load();
  }, [ticker, enabled, identity, load]);

  return { receipts, loading, error, refresh: load };
}
